import type { RegistrationVariant } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import type { OptionCategoryType } from "../api/optionCategories";
import { useCurrentOrganisation } from "../root/CurrentOrganisationProvider";
import { useNavigateWithQueryParams } from "./navigateWithQueryParams";

export const usePrivacyNavigate = () => {
  const paths = usePrivacyPaths();
  const navigate = useNavigateWithQueryParams();

  return { paths, navigate } as const;
};

const usePrivacyPaths = () => {
  const { slug } = useCurrentOrganisation();
  const fullPath = (pathname: string) => `/${slug}${pathname}`;

  return {
    root: () => fullPath(""),
    dashboard: () => fullPath("/dashboard"),

    registrationsGrid: ({ variant }: { variant: RegistrationVariant }) => fullPath(registrationBasePath(variant)),
    registrationsInline: ({ id, variant }: RegistrationIdAndVariant) =>
      fullPath(`${registrationBasePath(variant)}/${id}`),
    registrationsMultiStep: ({ id, variant }: RegistrationIdAndVariant) =>
      fullPath(`${registrationBasePath(variant)}/${id}/guided`),
    registrationsHistory: ({ id, variant }: RegistrationIdAndVariant) =>
      fullPath(`${registrationBasePath(variant)}/${id}/history`),
    registrationsUsersWithAccess: ({ id, variant }: RegistrationIdAndVariant) =>
      fullPath(`${registrationBasePath(variant)}/${id}/users-with-access`),
    registrationsDpiaRecommended: ({ id, variant }: RegistrationIdAndVariant) =>
      fullPath(`${registrationBasePath(variant)}/${id}/dpia-recommended`),
    assistant: () => fullPath("/assistant"),
    assistantNew: () => fullPath("/assistant/new"),
    assistantConversation: (conversationId: string) => fullPath(`/assistant/${conversationId}`),

    systemsGrid: () => fullPath("/systems"),
    systemsInline: (id: string) => fullPath(`/systems/${id}`),
    systemsConnectedRegistrations: (id: string) => fullPath(`/systems/${id}/registrations`),
    systemsConnectedProcessorRegistrations: (id: string) => fullPath(`/systems/${id}/registrations-processor`),
    systemsHistory: (id: string) => fullPath(`/systems/${id}/history`),
    dpiaGrid: () => fullPath("/dpia"),
    dpiaById: (id: string) => fullPath(`/dpia/${id}`),
    dpiaPreAssessmentById: (id: string) => fullPath(`/dpia/${id}/pre-assessment`),
    dpiaForm: (id: string) => fullPath(`/dpia/${id}/form`),
    dpiaHistory: (id: string) => fullPath(`/dpia/${id}/history`),
    measuresGrid: () => fullPath("/measures"),
    measuresInline: (id: string) => fullPath(`/measures/${id}`),
    measuresConnectedDpias: (id: string) => fullPath(`/measures/${id}/dpias`),
    measuresConnectedRisks: (id: string) => fullPath(`/measures/${id}/risks`),

    risksGrid: () => fullPath("/risks"),
    risksInline: (id: string) => fullPath(`/risks/${id}`),

    partnersGrid: () => fullPath("/partners"),
    partnersInline: (id: string) => fullPath(`/partners/${id}`),
    partnerComplianceManualHistory: ({ id }: { id: string }) => fullPath(`/partners/${id}/compliance/manual-history`),

    usersGrid: () => fullPath("/settings/people"),
    userById: (id: string) => fullPath(`/settings/people/${id}`),
    organisationUnits: () => fullPath("/settings/organisation-units"),
    organisationStructure: () => fullPath("/settings/organisation"),
    scimMapping: () => fullPath("/settings/scim-mapping"),

    pdcGrid: () => fullPath("/settings/personal-data-controllers"),
    pdcInline: (id: string) => fullPath(`/settings/personal-data-controllers/${id}`),
    templateForms: () => fullPath("/templates"),

    optionCategoryList: ({ categoryType }: { categoryType: OptionCategoryType }) =>
      fullPath(`/option-categories/${optionCategoryToSlug[categoryType]}`),

    informationTypesGrid: () => fullPath("/primary-assets"),
    informationTypesInline: (id: string) => fullPath(`/primary-assets/${id}`),
    informationTypesHistory: (id: string) => fullPath(`/primary-assets/${id}/history`),

    dataImport: () => fullPath("/data-import"),

    enterpriseCustomerOrganisations: () => fullPath("/customer/organisations"),
    enterpriseCustomerOrganisationById: (id: string) => fullPath(`/customer/organisations/${id}`),
    enterpriseCustomerOptionCategoryList: ({ categoryType }: { categoryType: OptionCategoryType }) =>
      fullPath(`/customer/option-categories/${customerOptionCategoryToSlug[categoryType]}`),

    sharedProcessingsGrid: () => fullPath(SHARED_PROCESSINGS_BASE_PATH),
    sharedProcessingsInline: (id: string) => fullPath(`${SHARED_PROCESSINGS_BASE_PATH}/${id}`),
    sharedProcessingsMultiStep: (id: string) => fullPath(`${SHARED_PROCESSINGS_BASE_PATH}/${id}/guided`),
    sharedProcessingsHistory: (id: string) => fullPath(`${SHARED_PROCESSINGS_BASE_PATH}/${id}/history`),
  } as const satisfies Record<string, (params: any) => string>;
};

export type Paths = ReturnType<typeof usePrivacyPaths>;

export const optionCategoryToSlug: Record<OptionCategoryType, string> = {
  categoriesOfPersonalData: "categories-of-personal-data",
  categoriesOfDataSubjects: "categories-of-data-subjects",
  projects: "projects",
  technicalSecurityMeasurements: "technical-security-measurements",
  categoriesOfProcessings: "categories-of-registrations",
};

export const customerOptionCategoryToSlug: Record<OptionCategoryType, string> = {
  ...optionCategoryToSlug,
  categoriesOfProcessings: "categories-of-processings",
};

type RegistrationIdAndVariant = { id: string; variant: RegistrationVariant };
const registrationBasePath = (variant: RegistrationVariant) =>
  variant === "Controller" ? "/registrations" : "/registrations-processor";

const SHARED_PROCESSINGS_BASE_PATH = "/customer/shared-processings";
